import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Storefront, Handshake, Crown, ArrowRight, Check, Sparkle } from "@phosphor-icons/react";
import { api } from "../lib/api";

/**
 * Public referral landing page: /parrainage/:code
 * - Stores the referral code in localStorage so it's automatically applied
 *   at the next `/dashboard/claim/:slug` call (see BusinessDetail auto-claim).
 * - Shows the sponsor business (if we can resolve it) as social proof.
 * - CTA to annuaire / partner offer.
 */
const STORAGE_REF = "mc68_referral";

export default function ReferralLanding() {
  const { code } = useParams();
  const [sponsor, setSponsor] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!code) return;
    // Persist code locally so BusinessDetail auto-claim can forward it
    try {
      localStorage.setItem(STORAGE_REF, code.toUpperCase());
      setSaved(true);
    } catch {
      /* localStorage disabled — silent */
    }
    // Resolve the sponsor business for social proof (best effort)
    api.get(`/public/referral/${encodeURIComponent(code)}`)
      .then((r) => setSponsor(r.data || null))
      .catch(() => setSponsor(null));
    document.title = "Parrainage — Mon Commerce 68";
  }, [code]);

  return (
    <div className="mx-auto max-w-3xl px-6 lg:px-10 py-16" data-testid="referral-landing">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="inline-flex items-center gap-2 glass-pill rounded-full px-3 py-1.5 mb-6">
          <Handshake size={14} className="text-secondary" weight="duotone" />
          <span className="label-uppercase !text-[0.65rem] !tracking-[0.2em]">Invitation partenaire</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-medium leading-tight mb-4">
          {sponsor ? (
            <><span className="text-gradient">{sponsor.name}</span> vous invite à rejoindre<br/>l'annuaire premium du 68.</>
          ) : (
            <>Vous êtes <span className="text-gradient">invité·e</span> à devenir partenaire.</>
          )}
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mb-8">
          Grâce à ce parrainage, votre sponsor <b>gagne 1 mois offert</b> dès que votre fiche passe Premium — et vous bénéficiez de l'offre lancement <b>−50 %</b> la 1ʳᵉ année. Gagnant-gagnant.
        </p>

        <div className="bg-card border-2 border-primary rounded-sm p-6 mb-8" data-testid="referral-code-box">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="label-uppercase mb-1">Code de parrainage</div>
              <div className="font-display text-2xl tracking-wide tabular-nums font-medium">{code}</div>
              {saved && (
                <div className="text-xs text-primary mt-2 inline-flex items-center gap-1"><Check size={12} weight="bold"/> Appliqué automatiquement à votre prochaine réclamation.</div>
              )}
            </div>
            {sponsor?.city && (
              <div className="text-right">
                <div className="label-uppercase mb-1">Parrain</div>
                <div className="text-sm font-semibold">{sponsor.name}</div>
                <div className="text-xs text-muted-foreground">{sponsor.city}</div>
              </div>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <Step n={1} icon={<Storefront size={22} weight="duotone"/>} title="Trouvez votre fiche" desc="Elle est probablement déjà référencée (25 k+ entreprises du 68)."/>
          <Step n={2} icon={<Sparkle size={22} weight="duotone"/>} title="Réclamez-la" desc="Connexion par simple email. Votre code parrainage est appliqué automatiquement."/>
          <Step n={3} icon={<Crown size={22} weight="duotone"/>} title="Passez Premium" desc="Votre sponsor reçoit 1 mois offert. Vous, vous gardez −50 % la 1ʳᵉ année."/>
        </div>

        <div className="flex flex-wrap gap-3">
          <Link to="/annuaire" className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-5 py-3 rounded-sm text-sm font-semibold transition-colors" data-testid="referral-cta-annuaire">
            <Storefront size={14} weight="fill"/> Trouver mon entreprise <ArrowRight size={12}/>
          </Link>
          <Link to="/passez-partenaire" className="inline-flex items-center gap-2 border-2 border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground px-5 py-3 rounded-sm text-sm font-semibold transition-colors" data-testid="referral-cta-pricing">
            Voir l'offre partenaire <ArrowRight size={12}/>
          </Link>
        </div>
      </motion.div>
    </div>
  );
}

function Step({ n, icon, title, desc }) {
  return (
    <div className="bg-accent/30 border border-border rounded-sm p-5" data-testid={`referral-step-${n}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 bg-primary text-primary-foreground rounded-sm grid place-items-center font-display font-semibold">{n}</div>
        <div className="text-primary">{icon}</div>
      </div>
      <div className="font-display text-base font-semibold mb-1">{title}</div>
      <div className="text-xs text-muted-foreground leading-relaxed">{desc}</div>
    </div>
  );
}
